import React, { useState, useEffect, useRef } from 'react';
import { taskDetailsService } from '../features/task/services/taskDetailsService';
import { getCurrentStatus, STATUS_NAME_REPORTED, STATUS_NAME_OPEN, STATUS_WORK_COMPLETED,STATUS_NAME_WORK_REPORT_COMPLETED,STATUS_NAME_WORK_ON_HOLD,STATUS_NAME_INSPECTION_SKIPPED,STATUS_NAME_ESTIMATION_SKIPPED,STATUS_NAME_ESTIMATION_APPROVED,STATUS_REJECTED, STATUS_WORK_HOLD} from '../shared/constants/statusConstants';
import { parseAndConvertToLocal, twentyFourHrFormat,convertMinutesToHoursAndMinutesShort } from '../shared/utils/dateFormatter';
import { formatCurrency as localFormatCurrency } from '../shared/utils/localeHelper';
import './StepperComponent.css';
import { getPriorityColor } from '../shared/utils/priorityHelper';
import { FiChevronDown, FiChevronUp, FiCalendar } from "react-icons/fi";

const StepperComponent = ({ taskId, isMobile, reportedDate, openDate, maintenanceData, statusCodes, onEstimationConfirm, onWorkReportConfirm, onFeedbackSubmit, signaturesLoading }) => {
  const [expandedStep, setExpandedStep] = useState(null);
  const [rating, setRating] = useState(0);
  const [comments, setComments] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);
  const drawingRef = useRef(false);
  const lastPointRef = useRef(null);
  const lastMidPointRef = useRef(null);

  const toggleStep = (index) => setExpandedStep(expandedStep === index ? null : index);

  const getServiceCategoryLabel = (value) => {
    const list = [{ label: 'PAID', value: 0 }, { label: 'UNDER WARRANTY', value: 1 }, { label: 'AMC', value: 3 }];
    return list.find(i => i.value === value)?.label || 'N/A';
  };

  const formatCurrency = (amount) => (amount !== null && amount !== undefined) ? localFormatCurrency(amount) : 'N/A';

  const getPriorityText = (priority) => {
    switch (priority) {
      case 0: return 'LOW';
      case 1: return 'MEDIUM';
      case 2: return 'NORMAL';
      case 3: return 'HIGH';
      case 4: return 'URGENT';
      default: return 'NORMAL';
    }
  };

  const setupSignatureCanvas = (canvasId, isReadOnly) => {
    const canvas = document.getElementById(canvasId);
    if (!canvas) return;
    // Fix resolution for retina/HiDPI screens
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0) {
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      const ctx = canvas.getContext('2d');
      ctx.scale(dpr, dpr);
    }
    const ctx = canvas.getContext('2d');
    ctx.strokeStyle = '#1a1a1a';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    canvas.style.cursor = isReadOnly ? 'default' : 'crosshair';
    canvas.style.pointerEvents = isReadOnly ? 'none' : 'auto';
    canvas.style.touchAction = 'none';
  };

  const clearSignature = (canvasId) => {
    const canvas = document.getElementById(canvasId);
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.beginPath();
    drawingRef.current = false;
  };

  useEffect(() => {
    if (expandedStep === null) return;
    const timer = setTimeout(() => {
      const estReadOnly = (maintenanceData?.estimationSignature?.length > 0);
      const wrReadOnly = (maintenanceData?.serviceReportSignature?.length > 0);
      setupSignatureCanvas('stepper-est-canvas', estReadOnly);
      setupSignatureCanvas('stepper-wr-canvas', wrReadOnly);

      const loadImg = (canvasId, url) => {
        if (!url) return;
        const canvas = document.getElementById(canvasId);
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          const dpr = window.devicePixelRatio || 1;
          const cssWidth = canvas.width / dpr;
          const cssHeight = canvas.height / dpr;
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(img, 5, 5, cssWidth - 10, cssHeight - 10);
        };
        img.src = url;
      };

      if (maintenanceData?.estimationSignature?.[0]) loadImg('stepper-est-canvas', maintenanceData.estimationSignature[0]);
      if (maintenanceData?.serviceReportSignature?.[0]) loadImg('stepper-wr-canvas', maintenanceData.serviceReportSignature[0]);

      const attachDrawing = (canvasId) => {
        const canvas = document.getElementById(canvasId);
        if (!canvas || canvas.style.pointerEvents === 'none') return;
        const getPos = (e) => {
          const rect = canvas.getBoundingClientRect();
          const src = e.touches ? e.touches[0] : e;
          return { x: src.clientX - rect.left, y: src.clientY - rect.top };
        };
        const onStart = (e) => {
          e.preventDefault();
          drawingRef.current = true;
          const p = getPos(e);
          lastPointRef.current = p;
          lastMidPointRef.current = p;
          const ctx = canvas.getContext('2d');
          ctx.beginPath();
          ctx.moveTo(p.x, p.y);
        };
        const onMove = (e) => {
          if (!drawingRef.current) return;
          e.preventDefault();
          const ctx = canvas.getContext('2d');
          const p = getPos(e);
          const last = lastPointRef.current;
          const mid = { x: (p.x + last.x) / 2, y: (p.y + last.y) / 2 };
          ctx.beginPath();
          ctx.moveTo(lastMidPointRef.current.x, lastMidPointRef.current.y);
          ctx.quadraticCurveTo(last.x, last.y, mid.x, mid.y);
          ctx.stroke();
          lastMidPointRef.current = mid;
          lastPointRef.current = p;
        };
        const onEnd = (e) => { e.preventDefault(); drawingRef.current = false; lastPointRef.current = null; lastMidPointRef.current = null; };
        canvas.addEventListener('mousedown', onStart, { passive: false });
        canvas.addEventListener('mousemove', onMove, { passive: false });
        canvas.addEventListener('mouseup', onEnd, { passive: false });
        canvas.addEventListener('mouseleave', onEnd, { passive: false });
        canvas.addEventListener('touchstart', onStart, { passive: false });
        canvas.addEventListener('touchmove', onMove, { passive: false });
        canvas.addEventListener('touchend', onEnd, { passive: false });
      };
      attachDrawing('stepper-est-canvas');
      attachDrawing('stepper-wr-canvas');
    }, 250);
    return () => clearTimeout(timer);
  }, [expandedStep, maintenanceData]);

  useEffect(() => {
    if (!taskId) return;
    taskDetailsService.getFeedbackDetails(taskId).then(data => {
      const existing = data?.data?.[0];
      if (existing) {
        setRating(existing.rating || 0);
        setComments(existing.comment || '');
        setSelectedTags(existing.tags || []);
        setFeedbackSubmitted(true);
      }
    }).catch(() => {});
  }, [taskId]);

  const handleEstConfirm = async (canvasId, nameId) => {
    const name = document.getElementById(nameId)?.value?.trim();
    if (!name) { alert('Name is required'); return; }
    const canvas = document.getElementById(canvasId);
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const hasSignature = ctx.getImageData(0,0,canvas.width,canvas.height).data.some(c => c !== 0);
    if (!hasSignature) { alert('Please sign to confirm'); return; }
    canvas.toBlob(blob => onEstimationConfirm && onEstimationConfirm(blob, name), 'image/png');
  };

  const handleWrConfirm = async (canvasId, nameId) => {
    const name = document.getElementById(nameId)?.value?.trim();
    if (!name) { alert('Name is required'); return; }
    const canvas = document.getElementById(canvasId);
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const hasSignature = ctx.getImageData(0,0,canvas.width,canvas.height).data.some(c => c !== 0);
    if (!hasSignature) { alert('Please sign to confirm'); return; }
    canvas.toBlob(async (blob) => {
      if (!blob) { alert('Failed to capture signature'); return; }
      try {
        await onWorkReportConfirm(blob, name);
      } catch (e) {
        console.error('Work report confirm error:', e);
        alert('Error confirming work report');
      }
    }, 'image/png');
  };

  const handleFeedbackSubmit = async () => {
    setIsSubmittingFeedback(true);
    try {
      const ok = await onFeedbackSubmit?.({ rating, comment: comments, tags: selectedTags });
      if (ok) setFeedbackSubmitted(true);
    } finally {
      setIsSubmittingFeedback(false);
    }
  };

  const isOnHold = statusCodes?.includes(STATUS_WORK_HOLD) && !statusCodes?.includes(32) && !statusCodes?.includes(34);

  const renderStepPanel = (step) => {
    const md = maintenanceData;
    const label = step.statusText;

    // Don't show panel for pending steps (not yet reached)
    if (step.status === 0 && label !== 'Reported') return null;

    if (label === 'Reported') return (
      <div className="step-panel rp-panel">
        {/* Request ID */}
        <div className="rp-request-row">
          <div>
            <div className="rp-label">Request ID</div>
            <div className="rp-req-id">
              #{md?.reqId || 'N/A'}
              {/* <span className="rp-date">
                &nbsp;&middot; {md?.reportedDate ? twentyFourHrFormat(parseAndConvertToLocal(md.reportedDate), true) : 'N/A'}
              </span> */}
            </div>
          </div>
          <span className="rp-priority-badge" style={{ background: getPriorityColor(md?.schedule?.priorityRaw) + '22', color: getPriorityColor(md?.schedule?.priorityRaw), border: `1px solid ${getPriorityColor(md?.schedule?.priorityRaw)}66` }}>
            <span className="rp-priority-dot" />
            {getPriorityText(md?.schedule?.priorityRaw)}
          </span>
        </div>

        {/* Customer */}
        <div className="rp-section-label">CUSTOMER</div>
        <div className="rp-divider" />
        <div className="rp-grid">
          <div><div className="rp-field-label">Name</div><div className="rp-field-value">{md?.companyName || 'N/A'}</div></div>
          <div><div className="rp-field-label">Phone</div><div className="rp-field-value">{md?.phoneNumber || 'N/A'}</div></div>
          <div><div className="rp-field-label">Email</div><div className="rp-field-value">{md?.email || 'N/A'}</div></div>
          <div><div className="rp-field-label">Address</div><div className="rp-field-value">{md?.address?.fullAddress || 'N/A'}</div></div>
        </div>

        {/* Equipment */}
        <div className="rp-section-label">EQUIPMENT</div>
        <div className="rp-divider" />
        <div className="rp-grid">
          <div><div className="rp-field-label">Equipment Name</div><div className="rp-field-value">{md?.equipment?.equipmentName || 'N/A'}</div></div>
          <div><div className="rp-field-label">Product</div><div className="rp-field-value">{md?.equipment?.product || 'N/A'}</div></div>

          <div><div className="rp-field-label">Make</div><div className="rp-field-value">{md?.equipment?.make || 'N/A'}</div></div>
          <div><div className="rp-field-label">Model</div><div className="rp-field-value">{md?.equipment?.model || 'N/A'}</div></div>
          {/* <div>
            <div className="rp-field-label">Warranty</div>
            <div className="rp-field-value"><span className="rp-warranty-badge">{getServiceCategoryLabel(md?.warrantyType)}</span></div>
          </div> */}
        </div>

        {/* Issues */}
        <div className="rp-section-label">ISSUES REPORTED</div>
        <div className="rp-divider" />
        <ul className="rp-issues-list">
          {md?.issues?.details?.length > 0
            ? md.issues.details.map((issue, i) => (
              <li key={i} className="rp-issue-item"><span className="rp-issue-dot">{i + 1}.</span>{issue}</li>
            ))
            : <li className="rp-issue-item"><span className="rp-issue-dot">1.</span>No issues reported</li>
          }
        </ul>

        {/* Scheduling */}
        <div className="rp-section-label">SCHEDULING</div>
        <div className="rp-divider" />
        <div className="rp-grid">
          <div><div className="rp-field-label">Scheduled</div><div className="rp-field-value">{md?.schedule?.scheduledDate && md.schedule.scheduledDate !== 'N/A' ? twentyFourHrFormat(parseAndConvertToLocal(md.schedule.scheduledDate), true) : 'N/A'}</div></div>
          <div><div className="rp-field-label">Technician</div><div className="rp-field-value">{md?.technician?.name || 'N/A'}</div></div>
        </div>
      </div>
    );

    if (label === 'Open' || label === 'Accepted') return null;

    if (label === 'Inspection skipped') return null;

    if (label === 'Inspection completed') return (
      <div className="step-panel">
        <p><b>Findings &amp; Recommendations</b></p>
        {md?.inspectionComments?.length > 0
          ? md.inspectionComments.map((item, i) => (
            <div key={i} style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', marginBottom: '8px', padding: '8px 10px', background: '#f8f9fa', borderRadius: '8px', border: '1px solid #e8e8e8' }}>
              <div style={{ minWidth: '22px', height: '22px', borderRadius: '50%', background: '#35605a', color: '#fff', fontSize: '11px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontWeight: 600 }}>{i + 1}</div>
              <div style={{ flex: 1 }}>
                {item.comments?.trim() && (
                  <p style={{ margin: '0 0 6px', fontSize: '13px', color: '#333', lineHeight: '1.6' }}>{item.comments}</p>
                )}
                {item.file && (
                  <img src={item.file} alt={`finding-${i}`} style={{ width: '100%', maxWidth: '200px', height: '130px', objectFit: 'cover', borderRadius: '6px', border: '1px solid #ddd' }} onError={e => e.target.style.display = 'none'} />
                )}
              </div>
            </div>
          ))
          : <p style={{ color: '#888', fontSize: '13px' }}>No findings available</p>
        }
      </div>
    );

    if (label === 'Estimation completed') return (
      <div className="step-panel">
        {md?.estimation ? (
          <>
            <p><b>Labour Cost:</b> {formatCurrency(md.estimation.labour_cost?.totalLabourCost)}</p>
            <p><b>Duration:</b> {convertMinutesToHoursAndMinutesShort(md.estimation.labour_cost?.durationInMinutes) || 'N/A'}</p>
            <p><b>Rate:</b> {formatCurrency(md.estimation.labour_cost?.rate)}</p>
            <p style={{marginTop:'8px'}}><b>Spare Parts:</b> {!md.estimation.spare_parts_cost?.length && <span style={{color:'#888'}}>N/A</span>}</p>
            {md.estimation.spare_parts_cost?.length > 0 && md.estimation.spare_parts_cost.map((part, i) => (
              <p key={i} style={{paddingLeft:'10px'}}>• {part?.name} (Qty: {part?.qty}) — {formatCurrency(part?.totalSpareCost)}</p>
            ))}
            <hr style={{border:'0.1px solid #555',margin:'10px 0'}} />
            <p><b>Grand Total: {formatCurrency(md.estimation.totalAmount)}</b></p>
          </>
        ) : <p>No estimation data</p>}
        <div style={{marginTop:'16px'}}>
          {md?.estimationSignature?.length > 0 ? (
            <>
              <p>Estimation Confirmed ✓</p>
              {md?.estimation?.confirmed_by && (
                <p style={{ fontSize: '13px', color: '#555', marginBottom: '4px' }}><b>Confirmed By:</b> {md.estimation.confirmed_by}</p>
              )}
              <canvas id="stepper-est-canvas" className='sign-canvas' />
            </>
          ) : (statusCodes?.includes(21) || statusCodes?.includes(22)) && statusCodes?.includes(24) && !isOnHold ? (
            <>
              <p>Sign to confirm estimation</p>
              <input type="text" id="stepper-est-name" placeholder="Name*" className='sign-name-input' />
              <canvas id="stepper-est-canvas" className='sign-canvas' />
              <div style={{ display: 'flex', gap: '8px', marginTop: '8px', justifyContent: 'flex-end' }}>
                <button style={{ padding: '6px 14px', borderRadius: '4px', border: '1px solid #ccc', background: '#f5f5f5', cursor: 'pointer' }} onClick={() => clearSignature('stepper-est-canvas')}>Clear</button>
                <button style={{ padding: '6px 14px', borderRadius: '4px', border: 'none', background: '#007bff', color: '#fff', cursor: 'pointer' }} onClick={() => handleEstConfirm('stepper-est-canvas','stepper-est-name')}>Confirm</button>
              </div>
            </>
          ) : null}
        </div>
      </div>
    );

    if (label === 'Work report completed') return (
      <div className="step-panel">
        <p><b>Work Done</b></p>
        {md?.workDoneComments?.length > 0
          ? md.workDoneComments.map((c, i) => (
            <div key={i} style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', marginBottom: '8px', padding: '8px 10px', background: '#f8f9fa', borderRadius: '8px', border: '1px solid #e8e8e8' }}>
              <div style={{ minWidth: '22px', height: '22px', borderRadius: '50%', background: '#35605a', color: '#fff', fontSize: '11px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontWeight: 600 }}>{i + 1}</div>
              <div style={{ flex: 1 }}>
                {(typeof c === 'object' ? c.comments : c) && (
                  <p style={{ margin: '0 0 6px', fontSize: '13px', color: '#333', lineHeight: '1.6' }}>{typeof c === 'object' ? c.comments : c}</p>
                )}
                {typeof c === 'object' && c.file && (
                  <img src={c.file} alt={`work-${i}`} style={{ width: '100%', maxWidth: '200px', height: '130px', objectFit: 'cover', borderRadius: '6px', border: '1px solid #ddd' }} onError={e => e.target.style.display = 'none'} />
                )}
              </div>
            </div>
          ))
          : <p>No comments</p>
        }
        {/* <p style={{marginTop:'10px'}}><b>Work Photos</b></p>
        <div className="step-panel-photo-grid">
          {md?.workReportFiles?.length > 0
            ? md.workReportFiles.map((url, i) => <img key={i} src={url} alt={`wr-${i}`} className="step-panel-photo" onError={e => e.target.style.display='none'} />)
            : <p>No photos</p>
          }
        </div> */}
        <p style={{marginTop:'10px'}}><b>Spare Parts:</b> {!md?.workReport?.spare_parts_cost?.length && <span style={{color:'#888'}}>N/A</span>}</p>
        {md?.workReport?.spare_parts_cost?.length > 0 && md.workReport.spare_parts_cost.map((part, i) => (
          <p key={i} style={{paddingLeft:'10px'}}>• {part?.name} (Qty: {part?.qty}) — {formatCurrency(part?.totalSpareCost)}</p>
        ))}
          <p><b>Labour:</b> {formatCurrency(md?.workReport?.labour_cost?.totalLabourCost)} ({convertMinutesToHoursAndMinutesShort(md?.workReport?.labour_cost?.durationInMinutes) || 'N/A'})</p>
        <hr style={{border:'0.1px solid #555',margin:'10px 0'}} />
        <p><b>Grand Total: {formatCurrency(md?.workReport?.totalAmount)}</b></p>
        <div style={{marginTop:'16px'}}>
          {md?.serviceReportSignature?.length > 0 ? (
            <>
              <p>Work Report Confirmed ✓</p>
              {md?.clientRepName && (
                <p style={{ fontSize: '13px', color: '#555', marginBottom: '4px' }}><b>Confirmed By:</b> {md.clientRepName}</p>
              )}
              <canvas id="stepper-wr-canvas" className='sign-canvas'/>
            </>
          ) : statusCodes?.includes(32) && !isOnHold ? (
            <>
              <p>Sign to confirm work report</p>
              <input type="text" id="stepper-wr-name" placeholder="Name*" className='sign-name-input' />
              <canvas id="stepper-wr-canvas" className='sign-canvas'/>
              <div style={{ display: 'flex', gap: '8px', marginTop: '8px', justifyContent: 'flex-end' }}>
                <button style={{ padding: '6px 14px', borderRadius: '4px', border: '1px solid #ccc', background: '#f5f5f5', cursor: 'pointer' }} onClick={() => clearSignature('stepper-wr-canvas')}>Clear</button>
                <button style={{ padding: '6px 14px', borderRadius: '4px', border: 'none', background: '#007bff', color: '#fff', cursor: 'pointer' }} onClick={() => handleWrConfirm('stepper-wr-canvas','stepper-wr-name')}>Confirm</button>
              </div>
            </>
          ) : null}
        </div>
      </div>
    );

    if (label === 'Work completed') {
      if (step.status !== STATUS_WORK_COMPLETED) return null;
      return (
      <div className="step-panel">
        {feedbackSubmitted ? (
          <>
            <p><b>Thank you for your feedback!</b></p>
            <div className="stars" style={{justifyContent:'flex-start',marginBottom:'12px'}}>
              {[1,2,3,4,5].map(s => (
                <span key={s} className={s <= rating ? 'active' : ''} style={{cursor:'default'}}>★</span>
              ))}
            </div>
            {selectedTags.length > 0 && (
              <div style={{marginBottom:'8px'}}>{selectedTags.join(', ')}</div>
            )}
            {comments && <p style={{marginBottom:'8px'}}>{comments}</p>}
          </>
        ) : (
          <>
            <p><b>Rate your experience</b></p>
            <div className="stars" style={{justifyContent:'flex-start',marginBottom:'12px'}}>
              {[1,2,3,4,5].map(s => (
                <span key={s} className={s <= rating ? 'active' : ''} onClick={() => setRating(s)} style={{cursor:'pointer'}}>★</span>
              ))}
            </div>
            <textarea placeholder="Comments" value={comments} onChange={e => setComments(e.target.value)} style={{width:'100%',minHeight:'60px',marginBottom:'8px', background:'white', border:'1px solid #aaa'}} />
            <button
              style={{padding:'6px 14px',borderRadius:'4px',border:'none',background:'#007bff',color:'#fff',cursor:'pointer'}}
              onClick={handleFeedbackSubmit} disabled={isSubmittingFeedback}>
              {isSubmittingFeedback ? 'Submitting...' : 'Submit'}
            </button>
          </>
        )}
      </div>
    );
    }
  };
  const [stepperList, setStepperList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (stepperList.length === 0) return;
    const workCompletedIndex = stepperList.findIndex(
      step => step.statusText === 'Work completed' && step.status === STATUS_WORK_COMPLETED
    );
    if (workCompletedIndex !== -1) {
      setExpandedStep(workCompletedIndex);
    }
  }, [stepperList]);
  const baseSteps = [
    { status: 0, statusText: 'Reported' },
    { status: 0, statusText: 'Open' },
    { status: 0, statusText: 'Accepted' },
    { status: 0, statusText: 'Inspection completed' },
    { status: 0, statusText: 'Estimation completed' },
    { status: 0, statusText: 'Estimation approved' },
    { status: 0, statusText: 'Work report completed' },
    { status: 0, statusText: 'Work completed' }
  ];

  const stepMapping = {
    'Inspection skipped': 'Inspection completed',
    'Estimation skipped': 'Estimation completed'
  };

  const getStepStatus = (status, statusText) => {
    if (status === STATUS_REJECTED || statusText === 'Rejected') return 'rejected';
    if (statusText === STATUS_NAME_WORK_ON_HOLD) return 'on-hold';
    if (statusText === 'Inspection skipped' || statusText === 'Estimation skipped') return 'skipped';
    const currentStatus = getCurrentStatus(status);
    if (currentStatus === 6) return 'completed';
    if (currentStatus === 4) return 'skipped';
    if (currentStatus === 1) return 'in-progress';
    if (currentStatus === 5) return 'rejected';
    if (currentStatus === 3) return 'on-hold';
    return 'pending';
  };

  const getConnectionColor = (currentStepStatus, nextStepStatus) => {
    if (currentStepStatus === 'completed' || currentStepStatus === 'in-progress') return '#00bd10';
    if (currentStepStatus === 'skipped') return 'rgba(52, 152, 219, 0.5)';
    if (currentStepStatus === 'rejected') return '#FF0000';
    if (currentStepStatus === 'on-hold') return '#F59E0B';
    return '#d2dcec';
  };

  const getFormattedDate = (dateString) => {
    if (!dateString) return '';
    const formatted = twentyFourHrFormat(parseAndConvertToLocal(dateString), true);
    return formatted.split(' ')[0];
  };

  const getFormattedTime = (dateString) => {
    if (!dateString) return '';
    const formatted = twentyFourHrFormat(parseAndConvertToLocal(dateString), true);
    return formatted.split(' ').slice(1).join(' ');
  };

  const getStepExtraInfo = (step) => {
    if (step.statusText === 'Assigned') {
      return `Assigned to ${step.assignedTo || 'Technician'}`;
    }
    if (step.statusText === 'Reported' && step.issueDescription) {
      return step.issueDescription;
    }
    return null;
  };

  const processStepperData = (receivedData) => {
    const uniqueMap = {};
    
    receivedData.forEach(item => {
      const key = item.status_text;
      if (!uniqueMap[key] || new Date(item.updated_at || item.created_at) > new Date(uniqueMap[key].updated_at || uniqueMap[key].created_at)) {
        uniqueMap[key] = item;
      }
    });

    const uniqueData = Object.values(uniqueMap).sort((a, b) => 
      new Date(a.created_at) - new Date(b.created_at)
    );

    // Build dynamic steps based on API response
    let steps = [...baseSteps];
    
    // Check for rejected status — only replace Accepted if no later Accepted exists (not reassigned)
    const rejectedItem = uniqueData.find(item => item.status === STATUS_REJECTED);
    const acceptedAfterRejection = rejectedItem && uniqueData.some(item =>
      (item.status === 16 || item.status_text === 'Accepted') &&
      new Date(item.created_at) > new Date(rejectedItem.created_at)
    );
    if (rejectedItem && !acceptedAfterRejection) {
      const acceptedIndex = steps.findIndex(step => step.statusText === 'Accepted');
      if (acceptedIndex !== -1) {
        steps[acceptedIndex] = {
          status: STATUS_REJECTED,
          statusText: 'Rejected',
          createdAt: rejectedItem.created_at,
          updatedAt: rejectedItem.updated_at
        };
      }
    } else if (rejectedItem && acceptedAfterRejection) {
      // Insert Rejected step before Accepted so both are visible
      const acceptedIndex = steps.findIndex(step => step.statusText === 'Accepted');
      if (acceptedIndex !== -1) {
        steps.splice(acceptedIndex, 0, {
          status: STATUS_REJECTED,
          statusText: 'Rejected',
          createdAt: rejectedItem.created_at,
          updatedAt: rejectedItem.updated_at
        });
      }
    }
    
    // Check for skipped steps and estimation approval
    const hasSkippedInspection = uniqueData.some(item => item.status_text === STATUS_NAME_INSPECTION_SKIPPED);
    const hasSkippedEstimation = uniqueData.some(item => item.status_text === STATUS_NAME_ESTIMATION_SKIPPED);
    const hasEstimationApproval = uniqueData.some(item => item.status_text === STATUS_NAME_ESTIMATION_APPROVED);
    
    // Remove estimation approval if not present in API or if estimation is skipped
    if (!hasEstimationApproval || hasSkippedEstimation) {
      steps = steps.filter(step => step.statusText !== STATUS_NAME_ESTIMATION_APPROVED);
    }
    
    // Check for Hold status - show if it ever existed in history (even if status moved on)
    const holdItem = uniqueData.find(item => item.status_text === STATUS_NAME_WORK_ON_HOLD);
    
    if (holdItem) {
      const workReportIndex = steps.findIndex(step => step.statusText === STATUS_NAME_WORK_REPORT_COMPLETED);
      if (workReportIndex !== -1) {
        steps.splice(workReportIndex, 0, {
          status: holdItem.status,
          statusText: STATUS_NAME_WORK_ON_HOLD,
          createdAt: holdItem.created_at,
          updatedAt: holdItem.updated_at
        });
      }
    }
    
    // Update steps with API data
    uniqueData.forEach(apiItem => {
      const mappedStep = stepMapping[apiItem.status_text] || apiItem.status_text;
      const stepIndex = steps.findIndex(step => step.statusText === mappedStep);
      
      if (stepIndex !== -1) {
        steps[stepIndex] = {
          status: apiItem.status,
          statusText: apiItem.status_text,
          createdAt: apiItem.created_at,
          updatedAt: apiItem.updated_at,
          assignedTo: apiItem.assigned_to,
          issueDescription: apiItem.issue_description
        };
      }
    });
    
    // Set dates for Reported and Open steps
    const reportedIndex = steps.findIndex(step => step.statusText === STATUS_NAME_REPORTED);
    if (reportedIndex !== -1 && reportedDate) {
      steps[reportedIndex].createdAt = reportedDate;
      steps[reportedIndex].status = STATUS_WORK_COMPLETED;
    }
    
    const openIndex = steps.findIndex(step => step.statusText === STATUS_NAME_OPEN);
    if (openIndex !== -1 && (reportedDate || openDate)) {
      steps[openIndex].createdAt = reportedDate || openDate;
      steps[openIndex].status = STATUS_WORK_COMPLETED;
    }
    
    return steps;
  };

  useEffect(() => {
    const fetchSteps = async () => {
      if (!taskId) return;

      try {
        setIsLoading(true);
        const response = await taskDetailsService.getMaintenanceRequestDetails(taskId, 'task_history');
        
        if (response.success && response.data) {
          const processedSteps = processStepperData(response.data);
          setStepperList(processedSteps);
        }
      } catch (error) {
        setErrorMessage(error.toString());
      } finally {
        setIsLoading(false);
      }
    };

    fetchSteps();
  }, [taskId, reportedDate, openDate]);

  if (isLoading) {
    return (
      <div className="stepper-loading">
        <div className="stepper-spinner"></div>
      </div>
    );
  }

  if (errorMessage) {
    return (
      <div className="stepper-error">
        Error: {errorMessage}
      </div>
    );
  }

  return (
  <div className={`stepper-wrapper ${isMobile ? 'mobile' : 'desktop'}`}>
    {stepperList.map((step, index) => {
      const stepNumber = (index + 1).toString().padStart(2, '0');
      const stepStatus = getStepStatus(step.status, step.statusText);
      const isFeedbackStep = step.statusText === 'Work completed';
      const isWorkCompleted = isFeedbackStep && step.status === STATUS_WORK_COMPLETED;
      const effectiveStatus = isWorkCompleted ? 'completed' : stepStatus;
      const extraInfo = getStepExtraInfo(step);
      const nextStep = index < stepperList.length - 1 ? stepperList[index + 1] : null;
      const nextStepStatus = nextStep ? getStepStatus(nextStep.status, nextStep.statusText) : null;
      const connectionColor = nextStep ? getConnectionColor(effectiveStatus, nextStepStatus) : null;
      const isOpen = expandedStep === index;
      const panel = renderStepPanel(step);

      return (
        <div className={`mainstepper ${effectiveStatus}`} key={index}>

          <div className="step-marker-wrapper">
            <div
              className="step-marker"
              style={{ backgroundColor: isWorkCompleted ? '#00bd10' : connectionColor }}
            >
              {stepNumber}
            </div>

            {index < stepperList.length - 1 && (
              <div
                className="step-connector"
                style={{ backgroundColor: effectiveStatus === 'on-hold' ? '#d2dcec' : connectionColor }}
              />
            )}
          </div>

          <div className="step-content">
            <div
        className="step-title-row" style={panel ? { cursor: "pointer" } : {}}  onClick={panel ? () => toggleStep(index) : undefined}
      >
        <span className="step-title">{step.statusText}</span>

        {panel && (
          <span className="step-toggle-icon">
            {isOpen ? <FiChevronUp size={15}/> : <FiChevronDown size={15}/>}
          </span>
        )}
      </div>

            {step.createdAt && (
              <span className="step-meta">
                <FiCalendar size={12} style={{marginRight:'4px',verticalAlign:'middle'}}/>{getFormattedDate(step.createdAt)} {getFormattedTime(step.createdAt)}
              </span>
            )}

            {extraInfo && (
              <span className="step-extra">{extraInfo}</span>
            )}

            {isOpen && panel}
          </div>

        </div>
      );
    })}
  </div>
);
};

export default StepperComponent;